import React, { ChangeEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
    CustomFlowbiteTheme,
    TextInput as FlowBiteTextInput,
    TextInputProps as FlowBiteTextInputProps,
} from 'flowbite-react';
import { textInputTheme } from '../../styles/themes/textInput';
import { Label } from '../label';
import { Text } from '../text';
import { useDebounce } from '../../hooks/use-debounce';
import { IconLoader, type IconName } from '../iconLoader';

export type InputType = 'text' | 'email' | 'password' | 'search' | 'url' | 'number' | 'time';
export type InputValue = string;
export type InputStep = number | 'any'; // 'any' for time type

type IconSlot = {
    iconName: IconName;
    label?: string;
    activate?: (event: React.MouseEvent) => void;
    testId?: string;
};

// Keeps the caret where it was: pressing an icon must not steal focus mid-edit.
const holdFocus = (e: { preventDefault: () => void }) => e.preventDefault();

// Enter and Space already activate the button; letting them bubble would drive the container too, since
// callers guard keys on the input element and these buttons are its siblings, not its children.
const containActivationKeys = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
        e.stopPropagation();
    }
};

const iconButtonClass =
    'flex cursor-pointer items-center rounded-sm border-none bg-transparent p-0 disabled:cursor-default' +
    ' focus-visible:outline focus-visible:outline-2 focus-visible:outline-system-primary';

// flowbite renders `icon`/`rightIcon` as element types, so an inline component would remount (and drop focus
// from) the icon button on every render. The slot's identity is fixed and it reads the latest render function.
const useStableSlot = (render: () => React.ReactNode): React.FC => {
    const renderRef = useRef(render);
    renderRef.current = render;
    return useMemo(() => {
        const Slot: React.FC = () => <>{renderRef.current()}</>;
        return Slot;
    }, []);
};

export interface InputProps
    extends Pick<
        FlowBiteTextInputProps,
        'sizing' | 'disabled' | 'helperText' | 'placeholder' | 'autoFocus' | 'onKeyDown'
    > {
    value?: InputValue;
    type?: InputType;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
    onDebouncedChange?: (event: ChangeEvent<HTMLInputElement>) => void;
    debounceTime?: number;
    testId?: string;
    id?: string;
    labelValue?: string;
    /** Shows a red * after the label and marks the input `aria-required`. */
    labelRequiredSymbol?: boolean;
    labelTestId?: string;
    ariaLabel?: string;
    leftIcon?: IconName;
    rightIcon?: IconName;
    onLeftIconClick?: () => void;
    /**
     * Accessible name for the left icon. With `onLeftIconClick` it renders the icon as a real button, so
     * the action is reachable by keyboard and announced (for a11y).
     */
    leftIconLabel?: string;
    // Takes the event so a handler can stop it reaching a clickable ancestor, e.g. a popover
    // trigger wrapping the field.
    onRightIconClick?: (event: React.MouseEvent) => void;
    /**
     * Accessible name for the right icon. Supplying it renders the icon as a real button, so the
     * action is reachable by keyboard and announced (for a11y).
     */
    rightIconLabel?: string;
    required?: boolean;
    minLength?: number;
    maxLength?: number;
    min?: number | string; // string for time
    max?: number | string; // string for time
    step?: InputStep;
    showErrorMessages?: boolean;
    errorMessage?: string;
    'aria-describedby'?: string;
    'aria-invalid'?: React.AriaAttributes['aria-invalid'];
    className?: string;
    inputBaseClassName?: string;
    clearable?: boolean;
    onBlur?: React.FocusEventHandler<HTMLInputElement> | undefined;
    onFocus?: React.FocusEventHandler<HTMLInputElement> | undefined;
    onFocusCapture?: React.FocusEventHandler<HTMLInputElement> | undefined;
    // Forwarded to the underlying input element, merged with the component's own
    // internal ref. Lets callers (e.g. an input-mask library) attach to the real
    // element without losing the component's clear-button focus behaviour.
    inputRef?: React.Ref<HTMLInputElement>;
    //additional props passed to the underlying input element
    [key: string]: unknown;
}

export const Input: React.FC<InputProps> = (props) => {
    const {
        value,
        type = 'text',
        onChange,
        onDebouncedChange,
        debounceTime = 300,
        leftIcon,
        testId,
        id,
        labelValue,
        labelRequiredSymbol = false,
        labelTestId,
        ariaLabel,
        onRightIconClick,
        rightIconLabel,
        onLeftIconClick,
        leftIconLabel,
        sizing = 'sm',
        disabled = false,
        helperText = '',
        placeholder,
        rightIcon,
        required = false,
        minLength,
        maxLength,
        min,
        max,
        step = 1,
        showErrorMessages = false,
        errorMessage,
        className,
        inputBaseClassName,
        autoFocus = false,
        onKeyDown,
        clearable = false,
        onBlur,
        onFocus,
        onFocusCapture,
        inputRef,
        // Pulled out of restProps so it can be merged with the helper text's id below rather than
        // overwriting it, since restProps is spread last.
        'aria-describedby': ariaDescribedBy,
        'aria-invalid': ariaInvalid,
        ...restProps
    } = props;

    const [validationError, setValidationError] = useState<string>('');

    const generatedId = useId();
    // fall back to a generated id so the label stays associated when the caller passes no id
    const inputId = id ?? generatedId;

    const internalInputRef = useRef<HTMLInputElement | null>(null);
    // Set both the internal ref (used by the clear button) and any forwarded ref.
    const setInputRef = useCallback(
        (node: HTMLInputElement | null) => {
            internalInputRef.current = node;
            if (typeof inputRef === 'function') {
                inputRef(node);
            } else if (inputRef) {
                (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
            }
        },
        [inputRef],
    );

    const errors = useMemo(
        () => ({
            required: 'A value is required',
            // text
            wrongLength: `Must be between ${minLength} and ${maxLength} characters`,
            tooShort: `Not less than ${minLength} characters`,
            tooLong: `Not more than ${maxLength} characters`,
            // number
            outOfRange: `Must be between ${min} and ${max}`,
            underMin: `Not smaller than ${min}`,
            overMax: `Not greater than ${max}`,
        }),
        [minLength, maxLength, min, max],
    );

    const validate = useCallback(
        (inputValue: InputValue, inputType: InputType) => {
            switch (true) {
                // required case
                case required && !inputValue.length:
                    setValidationError(errors.required);
                    break;
                // text cases
                case inputType !== 'number' &&
                    minLength !== undefined &&
                    maxLength !== undefined &&
                    (String(inputValue).length < minLength || String(inputValue).length > maxLength):
                    setValidationError(errors.wrongLength);
                    break;
                case inputType !== 'number' && minLength !== undefined && String(inputValue).length < minLength:
                    setValidationError(errors.tooShort);
                    break;
                case inputType !== 'number' && maxLength !== undefined && String(inputValue).length > maxLength:
                    setValidationError(errors.tooLong);
                    break;
                // number cases
                case inputType === 'number' &&
                    min !== undefined &&
                    max !== undefined &&
                    (Number(inputValue) < Number(min) || Number(inputValue) > Number(max)):
                    setValidationError(errors.outOfRange);
                    break;
                case inputType === 'number' && min !== undefined && Number(inputValue) < Number(min):
                    setValidationError(errors.underMin);
                    break;
                case inputType === 'number' && max !== undefined && Number(inputValue) > Number(max):
                    setValidationError(errors.overMax);
                    break;
                // no error case
                default:
                    setValidationError('');
            }
        },
        [required, errors, minLength, maxLength, min, max],
    );

    useEffect(() => {
        if (required && !value?.length) {
            setValidationError(errors.required);
        }
    }, [errors.required, required, value]);

    // An externally supplied message always shows; the built-in one stays gated on
    // showErrorMessages, and either turns the field red.
    const shownError = errorMessage ?? (showErrorMessages && validationError ? validationError : undefined);
    const errorState = Boolean(errorMessage) || Boolean(validationError);
    const stateThemeColour = errorState ? 'failure' : 'gray';
    const iconColorClasses = errorState
        ? 'fill-system-error stroke-system-error'
        : 'fill-system-on-surface-variant stroke-system-on-surface-variant';

    // debounce handler
    const debouncedOnChange = useDebounce((event: ChangeEvent<HTMLInputElement>) => {
        if (onDebouncedChange) {
            onDebouncedChange(event);
        }
    }, debounceTime);

    const handleOnChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const newValue = event.target.value;
            validate(newValue, type);
            // Immediate state updates
            if (onChange) {
                onChange(event);
            }
            // Debounced side effects
            if (onDebouncedChange) {
                debouncedOnChange(event);
            }
        },
        [validate, onChange, onDebouncedChange, debouncedOnChange, type],
    );

    const handleClear = useCallback(() => {
        const e = { target: { value: '' }, currentTarget: { value: '' } } as ChangeEvent<HTMLInputElement>;
        handleOnChange(e);
        //autofocus the input after clearing
        internalInputRef.current?.focus();
    }, [handleOnChange]);

    // Both icons follow one rule: a label plus something to activate makes a real button; a handler
    // alone keeps the plain click target; neither makes it decorative.
    const renderIconSlot = (slot: IconSlot) => {
        const asButton = Boolean(slot.label) && Boolean(slot.activate);
        const icon = (
            <IconLoader
                className={`shrink-0 ${slot.activate ? 'cursor-pointer' : 'cursor-default'} ${iconColorClasses}`}
                size="sm"
                iconName={slot.iconName}
                data-testid={slot.testId}
                // Left to the button when there is one, or the click would fire twice
                onClick={asButton ? undefined : slot.activate}
                onMouseDown={holdFocus}
                aria-hidden="true"
            />
        );

        if (!asButton) {
            return icon;
        }

        return (
            <button
                type="button"
                aria-label={slot.label}
                disabled={disabled}
                onClick={slot.activate}
                onMouseDown={holdFocus}
                onKeyDown={containActivationKeys}
                className={iconButtonClass}
            >
                {icon}
            </button>
        );
    };

    const isClear = clearable && Boolean(value);
    // a clearable input with no rightIcon has nothing to show while it is empty; without this guard
    // IconLoader is rendered with `iconName={undefined}` and logs "Icon not found" on every keystroke
    const rightIconName = isClear ? 'IconRemove' : rightIcon;
    const LeftIconSlot = useStableSlot(() =>
        leftIcon ? renderIconSlot({ iconName: leftIcon, label: leftIconLabel, activate: onLeftIconClick }) : null,
    );
    const RightIconSlot = useStableSlot(() =>
        rightIconName
            ? renderIconSlot({
                  iconName: rightIconName,
                  label: rightIconLabel,
                  activate: isClear ? handleClear : onRightIconClick,
                  testId: testId && isClear ? `${testId}-clear` : undefined,
              })
            : null,
    );

    const themeWithIconClasses: CustomFlowbiteTheme['textInput'] = {
        ...textInputTheme,
        field: {
            ...textInputTheme?.field,
            icon: {
                ...textInputTheme?.field?.icon,
                svg: `${textInputTheme?.field?.icon?.svg} ${iconColorClasses}`,
            },
            input: {
                ...textInputTheme?.field?.input,
                base: `${textInputTheme?.field?.input?.base} ${inputBaseClassName}`,
            },
        },
    };
    const ref = useRef(null);

    const renderHelperText = Boolean(helperText) || Boolean(shownError);
    const showingError = Boolean(shownError);
    const helperTextId = `${inputId}-helper-text`;
    // Only announced while it holds an error; plain helper text would otherwise interrupt.
    const helperTextRole = showingError ? 'alert' : undefined;
    const describedBy =
        [ariaDescribedBy, renderHelperText ? helperTextId : undefined].filter(Boolean).join(' ') || undefined;

    // Rendered here rather than through flowbite's helperText prop, which wraps it in mt-2 text-sm
    // and gives no id for aria-describedby. font-sans is explicit because Preflight is off: without
    // it this falls back to the browser's default serif.
    const helperTextElement = renderHelperText && (
        <p
            id={helperTextId}
            role={helperTextRole}
            className={`mt-1 text-body-small font-sans ${
                showingError ? 'text-system-error' : 'text-system-on-surface-variant'
            }`}
        >
            {shownError ?? helperText}
        </p>
    );

    return (
        <div className={className} ref={ref}>
            {labelValue && (
                <div className="mb-2 flex flex-row">
                    <Label value={labelValue} disabled={disabled} testId={labelTestId} componentId={inputId} />
                    {labelRequiredSymbol && (
                        // Hidden from assistive tech, which gets `aria-required` on the input instead.
                        <div aria-hidden="true">
                            <Text className="text-system-error pl-0.5">*</Text>
                        </div>
                    )}
                </div>
            )}
            <FlowBiteTextInput
                ref={setInputRef}
                type={type}
                value={value}
                onChange={handleOnChange}
                id={inputId}
                data-testid={testId}
                aria-label={ariaLabel}
                color={stateThemeColour}
                sizing={sizing}
                disabled={disabled}
                placeholder={placeholder}
                icon={leftIcon ? LeftIconSlot : undefined}
                rightIcon={rightIcon || clearable ? RightIconSlot : undefined}
                required={required}
                minLength={minLength}
                maxLength={maxLength}
                min={min}
                max={max}
                step={step}
                theme={themeWithIconClasses}
                autoFocus={autoFocus}
                onKeyDown={onKeyDown}
                onBlur={onBlur}
                onFocus={onFocus}
                onFocusCapture={onFocusCapture}
                aria-required={labelRequiredSymbol || undefined}
                {...restProps}
                aria-describedby={describedBy}
                aria-invalid={ariaInvalid ?? (errorState || undefined)}
            />
            {helperTextElement}
        </div>
    );
};
